// src/Components/Menu/Menu.tsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../../Redux/Store';
import {
  Container,
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Paper,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import PreviewIcon from '@mui/icons-material/Preview';
import { createApiRequest } from '../../utils/apiUtils';

// MenuCategory 
import {
  fetchMenuCategories,
  reorderMenuCategories
} from '../../Redux/MenuCategory/menuCategoryApi';
import { swapCategoriesLocally } from '../../Redux/MenuCategory/menuCategorySlice';

// Menu
import { fetchMenus } from '../../Redux/Menu/menuApi';

import Header from '../../Components/Header/Header';
import MenuAttachment from './MenuAttachment';
import CategoryManagement from './MenuCategory/CategoryManagement';
import MenuManagement from './MenuManagement';

const Menu: React.FC = () => {
  const navigate = useNavigate();
  const { store_id } = useParams<{ store_id: string }>();
  const dispatch: AppDispatch = useDispatch();

  const menuCategories = useSelector((state: RootState) => state.menuCategory.categories);
  const menus = useSelector((state: RootState) => state.menu.menus);

  // オーダー画面テンプレート（店舗設定・BE ADR 0068 / feast-fe ADR 0033）。
  const [orderTemplate, setOrderTemplate] = useState<'IMAGE' | 'TEXT'>('IMAGE');
  const [templateError, setTemplateError] = useState<string | null>(null);
  useEffect(() => {
    if (!store_id) return;
    createApiRequest<{ template: 'IMAGE' | 'TEXT' }>(`/stores/${store_id}/order-menu-template`, {
      method: 'GET',
      requiresAuth: true,
    })
      .then((data) => setOrderTemplate(data.template === 'TEXT' ? 'TEXT' : 'IMAGE'))
      .catch(() => undefined); // 未対応 BE では既定(IMAGE)のまま
  }, [store_id]);

  const handleTemplateChange = (_e: React.MouseEvent<HTMLElement>, value: 'IMAGE' | 'TEXT' | null) => {
    if (!store_id || !value || value === orderTemplate) return;
    const prev = orderTemplate;
    setOrderTemplate(value);
    setTemplateError(null);
    createApiRequest<{ template: string }>(`/stores/${store_id}/order-menu-template`, {
      method: 'PUT',
      requiresAuth: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ template: value }),
    }).catch(() => {
      setOrderTemplate(prev);
      setTemplateError('テンプレートの保存に失敗しました。時間をおいて再度お試しください。');
    });
  };

  const [activeTab, setActiveTab] = useState<'categories' | 'menus' | 'attachments'>('categories');
  const [error, setError] = useState<string | null>(null);

  const [newCategoryName, setNewCategoryName] = useState('');
  const [newMenuName, setNewMenuName] = useState<string>('');
  const [newMenuPrice, setNewMenuPrice] = useState<number | ''>('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [toggleView, setToggleView] = useState<'attached' | 'unattached'>('attached');

  useEffect(() => {
    if (!store_id) {
      setError('store_id is undefined');
      return;
    }
    dispatch(fetchMenuCategories(store_id)).catch((err) => setError(String(err)));
    dispatch(fetchMenus(store_id)).catch((err) => setError(String(err)));
  }, [dispatch, store_id]);

  const handleCategoryDragEnd = async (result: { source: { index: number }; destination?: { index: number } }) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.index === destination.index) return;

    // ローカルで並び替え
    dispatch(swapCategoriesLocally({ fromIndex: source.index, toIndex: destination.index }));
    
    // 更新後のカテゴリを取得
    const updatedCategories = Array.from(menuCategories);
    
    // 並び順を明示的に更新
    const reorderedCategories = updatedCategories.map((category, index) => ({
      ...category,
      sortIndex: index + 1,
      items: category.items || []
    }));
    
    // カテゴリの並び順をサーバーに保存
    try {
      await dispatch(reorderMenuCategories({
        storeId: store_id!,
        reorderedCategories,
      })).unwrap();
      
      // 保存成功後、最新データを再取得して順序を確実に同期
      await dispatch(fetchMenuCategories(store_id!)).unwrap();
      setError(null);
    } catch (error) {
      console.error('Failed to reorder categories:', error);
      setError('カテゴリの並び替えに失敗しました。');
    }
  };

  const currentCategory = menuCategories?.length > 0
    ? menuCategories.find((cat) => cat?.id && cat.id === selectedCategory)
    : null;

  // カテゴリのitemsの順序を保持するため、itemsの配列をそのまま使用
  const attachedMenus = currentCategory?.items
    ? currentCategory.items
        .map((item) => menus.find((m) => m.id === item.id))
        .filter((m): m is NonNullable<typeof m> => m !== undefined)
    : [];

  const unattachedMenus = currentCategory?.items
    ? menus.filter((m) =>
        !currentCategory.items?.some((item) => item?.id && item.id === m.id)
      )
    : menus;
  const filteredMenus = toggleView === 'attached' ? attachedMenus : unattachedMenus;

  const handlePreviewClick = () => {
    navigate(`/${store_id}/menu/preview`);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: 'categories' | 'menus' | 'attachments') => {
    setActiveTab(newValue);
  };

  return (
    <Box>
      <Header />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h4" component="h1" sx={{ whiteSpace: 'nowrap' }}>
            メニュー管理
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate(`/${store_id}/manage/menu-options`)}
            >
              メニューオプション管理
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PreviewIcon />}
              onClick={handlePreviewClick}
            >
              プレビュー
            </Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {templateError && <Alert severity="error" sx={{ mb: 2 }}>{templateError}</Alert>}

        {/* オーダー画面テンプレート（店舗設定）。画像を用意しない店舗はテキスト表示に切替できる。 */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="subtitle2">オーダー画面の表示テンプレート</Typography>
            <Typography variant="caption" color="text.secondary">
              「画像なし」を選ぶと、お客様・店員オーダー画面が品名と価格のリスト表示になります（メニュー画像の登録は引き続き可能です）。
            </Typography>
          </Box>
          <ToggleButtonGroup
            exclusive
            color="primary"
            size="small"
            value={orderTemplate}
            onChange={handleTemplateChange}
          >
            <ToggleButton value="IMAGE">画像つき</ToggleButton>
            <ToggleButton value="TEXT">画像なし</ToggleButton>
          </ToggleButtonGroup>
        </Paper>

        {store_id && (
          <Paper sx={{ mb: 3 }}>
            <Tabs
              sx={{ '& .MuiTab-root': { whiteSpace: 'nowrap', minWidth: 0, px: 1 } }}
              value={activeTab}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
              variant="fullWidth"
            >
              <Tab value="categories" label="カテゴリ管理" />
              <Tab value="menus" label="メニュー管理" />
              <Tab value="attachments" label="メニューの紐付け" />
            </Tabs>

            <Box sx={{ p: 3 }}>
              {activeTab === 'categories' && (
                <CategoryManagement
                  newCategoryName={newCategoryName}
                  setNewCategoryName={setNewCategoryName}
                  onDragEnd={handleCategoryDragEnd}
                />
              )}

              {activeTab === 'menus' && (
                <MenuManagement
                  newMenuName={newMenuName}
                  newMenuPrice={newMenuPrice}
                  setNewMenuName={setNewMenuName}
                  setNewMenuPrice={setNewMenuPrice}
                />
              )}

              {activeTab === 'attachments' && (
                <MenuAttachment
                  menuCategories={menuCategories}
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  toggleView={toggleView}
                  setToggleView={setToggleView}
                  filteredMenus={filteredMenus}
                />
              )}
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default Menu;